(function () {
  'use strict';

  if (window.UnusedFuelCashRewards) return;

  const EXPIRY_SOON_HOUR_THRESHOLD = 12;
  const EXPIRY_SOON_DAY_THRESHOLD = 3;
  const EXPIRED_REWARD_VISIBILITY_DAYS = 60;
  const API_URL = '/apps/fg-unused-rewards/get-rewards';

  function parseExpiry(value) {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatRelativeExpiry(expiresAt) {
    const diffMs = expiresAt.getTime() - Date.now();
    if (diffMs <= 0) return { text: 'Expired', soon: true };
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    if (minutes < 1) return { text: 'Expires in less than a minute', soon: true };
    if (minutes < 60) return { text: `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`, soon: true };
    if (hours < 24) return { text: `Expires in ${hours} hour${hours === 1 ? '' : 's'}`, soon: hours <= EXPIRY_SOON_HOUR_THRESHOLD };
    if (days < 30) return { text: `Expires in ${days} day${days === 1 ? '' : 's'}`, soon: days <= EXPIRY_SOON_DAY_THRESHOLD };
    if (days < 365) return { text: `Expires in ${months} month${months === 1 ? '' : 's'}`, soon: false };
    return { text: `Expires in ${years} year${years === 1 ? '' : 's'}`, soon: false };
  }

  function formatExactExpiry(expiresAt) {
    return expiresAt.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  function formatRelativeAgo(expiredAt) {
    const diffMs = Date.now() - expiredAt.getTime();
    if (diffMs <= 0) return 'Expired';
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    if (hours < 24) return 'Expired today';
    if (days < 30) return `Expired ${days} day${days === 1 ? '' : 's'} ago`;
    if (days < 365) return `Expired ${months} month${months === 1 ? '' : 's'} ago`;
    return `Expired ${years} year${years === 1 ? '' : 's'} ago`;
  }

  function partitionRewards(rewards) {
    const nowMs = Date.now();
    const expiredCutoffMs = nowMs - EXPIRED_REWARD_VISIBILITY_DAYS * 24 * 60 * 60 * 1000;
    const actives = [];
    const expireds = [];
    rewards.forEach((reward) => {
      if (!reward) return;
      const expiresAt = parseExpiry(reward.expiresAt);
      const isExpired = reward.rewardStatus === 'expired' || (expiresAt && expiresAt.getTime() <= nowMs);
      if (isExpired) {
        if (expiresAt && expiresAt.getTime() >= expiredCutoffMs) expireds.push(reward);
      } else {
        actives.push(reward);
      }
    });
    actives.sort((a, b) => {
      const aT = parseExpiry(a && a.expiresAt)?.getTime() ?? Infinity;
      const bT = parseExpiry(b && b.expiresAt)?.getTime() ?? Infinity;
      return aT - bT;
    });
    expireds.sort((a, b) => {
      const aT = parseExpiry(a && a.expiresAt)?.getTime() ?? 0;
      const bT = parseExpiry(b && b.expiresAt)?.getTime() ?? 0;
      return bT - aT;
    });
    return { actives, expireds };
  }

  // Copy text to the clipboard; falls back to execCommand('copy') when the
  // Clipboard API is missing or rejects. Resolves true on success.
  function copyText(text) {
    const legacyCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      ta.remove();
      return ok;
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(() => true, legacyCopy);
    }
    return Promise.resolve(legacyCopy());
  }

  // Tap-to-copy coupon code: the code itself plus a visible "Copy" label.
  function buildCopyCode(discountCode) {
    const wrap = document.createElement('div');
    wrap.className = 'ufc-reward-code-wrap';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ufc-reward-code';
    button.setAttribute('aria-label', `Copy code ${discountCode}`);
    const codeText = document.createElement('span');
    codeText.className = 'ufc-reward-code-text';
    codeText.textContent = discountCode;
    const label = document.createElement('span');
    label.className = 'ufc-reward-code-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Copy';
    button.appendChild(codeText);
    button.appendChild(label);

    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    let resetTimer;
    button.addEventListener('click', () => {
      copyText(discountCode).then((ok) => {
        clearTimeout(resetTimer);
        if (ok) {
          button.classList.add('is-copied');
          label.textContent = 'Copied';
          status.textContent = `Copied ${discountCode} to clipboard`;
        } else {
          // Couldn't copy: select the code so it can be copied manually
          const range = document.createRange();
          range.selectNodeContents(codeText);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          label.textContent = 'Selected';
          status.textContent = `Code ${discountCode} selected, copy it manually`;
        }
        resetTimer = setTimeout(() => {
          button.classList.remove('is-copied');
          label.textContent = 'Copy';
          status.textContent = '';
        }, 2000);
      });
    });

    wrap.appendChild(button);
    wrap.appendChild(status);
    return wrap;
  }

  function buildActiveCard(reward, discountStates) {
    const discountCode = reward.status;
    const codeUpper = discountCode ? discountCode.toUpperCase() : '';
    const isApplied = discountStates.applied.includes(codeUpper);
    const isInapplicable = discountStates.inapplicable.includes(codeUpper);

    const card = document.createElement('div');
    card.className = 'ufc-reward-card';
    if (isApplied) card.classList.add('applied');
    else if (isInapplicable) card.classList.add('inapplicable');

    const action = document.createElement('div');
    action.className = 'ufc-reward-action';
    action.textContent = reward.action || '';

    card.appendChild(action);
    card.appendChild(discountCode ? buildCopyCode(discountCode) : document.createElement('div'));

    const expiresAt = parseExpiry(reward.expiresAt);
    if (expiresAt) {
      const { text: relativeText, soon } = formatRelativeExpiry(expiresAt);
      const expiry = document.createElement('div');
      expiry.className = 'ufc-reward-expiry';
      if (soon) expiry.classList.add('expiring-soon');
      const relative = document.createElement('div');
      relative.className = 'ufc-reward-expiry-relative';
      relative.textContent = relativeText;
      const exact = document.createElement('div');
      exact.className = 'ufc-reward-expiry-date';
      exact.textContent = formatExactExpiry(expiresAt);
      expiry.appendChild(relative);
      expiry.appendChild(exact);
      card.appendChild(expiry);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ufc-reward-button unused-reward-coupon-code';
    button.setAttribute('data-unused-reward-coupon-code', discountCode || '');
    if (isApplied) {
      button.textContent = 'Applied';
      button.disabled = true;
    } else if (isInapplicable) {
      button.textContent = 'Not valid for your cart contents';
      button.disabled = true;
      button.classList.add('inapplicable');
    } else {
      button.textContent = 'Apply to cart';
      button.disabled = false;
    }
    card.appendChild(button);
    return card;
  }

  function buildExpiredCard(reward) {
    const expiredAt = parseExpiry(reward.expiresAt);
    const card = document.createElement('div');
    card.className = 'ufc-reward-card expired';

    const action = document.createElement('div');
    action.className = 'ufc-reward-action';
    action.textContent = reward.action || '';

    const code = document.createElement('div');
    code.className = 'ufc-reward-code';
    code.textContent = reward.status || '';

    card.appendChild(action);
    card.appendChild(code);

    if (expiredAt) {
      const expiry = document.createElement('div');
      expiry.className = 'ufc-reward-expiry';
      const relative = document.createElement('div');
      relative.className = 'ufc-reward-expiry-relative';
      relative.textContent = formatRelativeAgo(expiredAt);
      const exact = document.createElement('div');
      exact.className = 'ufc-reward-expiry-date';
      exact.textContent = formatExactExpiry(expiredAt);
      expiry.appendChild(relative);
      expiry.appendChild(exact);
      card.appendChild(expiry);
    }

    return card;
  }

  async function getDiscountStates() {
    const utils = window.YotpoDiscountCodeUtils;
    if (!utils || typeof utils.waitForYotpoUtils !== 'function') {
      return { applied: [], inapplicable: [] };
    }
    return new Promise((resolve) => {
      utils.waitForYotpoUtils(async () => {
        if (typeof utils.getDiscountStates === 'function') {
          try {
            resolve(await utils.getDiscountStates());
            return;
          } catch (e) {
            console.error('UnusedFuelCashRewards: getDiscountStates failed', e);
          }
        }
        resolve({ applied: [], inapplicable: [] });
      });
    });
  }

  function setupClickHandlers(buttonSelector) {
    const utils = window.YotpoDiscountCodeUtils;
    if (utils && typeof utils.setupRewardClickHandlers === 'function') {
      utils.setupRewardClickHandlers({
        buttonSelector,
        cardSelector: '.ufc-reward-card',
        retryCount: 1,
        retryDelay: 300
      });
    }
  }

  function setupCartUpdateListener(buttonSelector) {
    const utils = window.YotpoDiscountCodeUtils;
    if (!utils || typeof utils.waitForYotpoUtils !== 'function') return;
    utils.waitForYotpoUtils(() => {
      const refresh = async () => {
        if (typeof utils.updateRewardButtonStates !== 'function' || typeof utils.getDiscountStates !== 'function') return;
        const discountStates = await utils.getDiscountStates();
        utils.updateRewardButtonStates(discountStates, {
          buttonSelector,
          cardSelector: '.ufc-reward-card',
          notAppliedText: 'Apply to cart'
        });
      };
      if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, refresh);
      }
      document.addEventListener('cart:updated', refresh);
    });
  }

  function isModalRoot(root) {
    return root.classList.contains('ufc-section--modal');
  }

  function showLoading(root) {
    const loading = root.querySelector('.ufc-loading');
    const title = root.querySelector('.ufc-title');
    const description = root.querySelector('.ufc-description');
    const empty = root.querySelector('.ufc-empty');
    const activeContainer = root.querySelector('.ufc-active-container');
    const expiredSection = root.querySelector('.ufc-expired-section');
    const expiredGrid = expiredSection ? expiredSection.querySelector('.ufc-rewards-grid') : null;

    if (title) title.style.display = isModalRoot(root) ? 'block' : 'none';
    if (description) description.style.display = 'none';
    if (empty) empty.style.display = 'none';
    if (activeContainer) activeContainer.innerHTML = '';
    if (expiredSection) expiredSection.style.display = 'none';
    if (expiredGrid) expiredGrid.innerHTML = '';
    if (loading) loading.style.display = 'flex';
  }

  function showEmpty(root) {
    const loading = root.querySelector('.ufc-loading');
    const title = root.querySelector('.ufc-title');
    const description = root.querySelector('.ufc-description');
    const empty = root.querySelector('.ufc-empty');
    const activeContainer = root.querySelector('.ufc-active-container');
    const expiredSection = root.querySelector('.ufc-expired-section');
    const expiredGrid = expiredSection ? expiredSection.querySelector('.ufc-rewards-grid') : null;

    if (loading) loading.style.display = 'none';
    if (title) title.style.display = isModalRoot(root) ? 'block' : 'none';
    if (description) description.style.display = 'none';
    if (empty) empty.style.display = 'block';
    if (activeContainer) activeContainer.innerHTML = '';
    if (expiredSection) expiredSection.style.display = 'none';
    if (expiredGrid) expiredGrid.innerHTML = '';
  }

  async function render(root, variant) {
    const buttonSelector = `[data-ufc-root="${root.dataset.ufcRoot}"] .unused-reward-coupon-code`;

    showLoading(root);
    let data;
    try {
      const response = await fetch(API_URL);
      data = await response.json();
    } catch (error) {
      console.error('UnusedFuelCashRewards: fetch failed', error);
      showEmpty(root);
      return;
    }

    if (!data || !data.success || !Array.isArray(data.unusedRewards) || data.unusedRewards.length === 0) {
      showEmpty(root);
      return;
    }

    const discountStates = await getDiscountStates();
    const { actives, expireds } = partitionRewards(data.unusedRewards);

    if (actives.length === 0 && expireds.length === 0) {
      showEmpty(root);
      return;
    }

    const loading = root.querySelector('.ufc-loading');
    const title = root.querySelector('.ufc-title');
    const description = root.querySelector('.ufc-description');
    const empty = root.querySelector('.ufc-empty');
    const activeContainer = root.querySelector('.ufc-active-container');
    const expiredSection = root.querySelector('.ufc-expired-section');
    const expiredGrid = expiredSection ? expiredSection.querySelector('.ufc-rewards-grid') : null;

    if (loading) loading.style.display = 'none';
    if (title) title.style.display = 'block';
    if (description && description.textContent.trim()) {
      description.style.display = actives.length > 0 ? 'block' : 'none';
    }
    if (empty) empty.style.display = 'none';
    if (activeContainer) activeContainer.innerHTML = '';

    if (actives.length > 0 && activeContainer) {
      const grid = document.createElement('div');
      grid.className = 'ufc-rewards-grid';
      actives.forEach((reward) => grid.appendChild(buildActiveCard(reward, discountStates)));
      activeContainer.appendChild(grid);
    }

    if (expiredGrid) expiredGrid.innerHTML = '';
    if (expireds.length > 0 && expiredGrid && expiredSection) {
      expireds.forEach((reward) => expiredGrid.appendChild(buildExpiredCard(reward)));
      expiredSection.style.display = 'flex';
    } else if (expiredSection) {
      expiredSection.style.display = 'none';
    }

    if (actives.length > 0) setupClickHandlers(buttonSelector);
    applyPresentationState(root, actives, expireds);
  }

  function applyPresentationState(root, actives, expireds) {
    const title = root.querySelector('.ufc-title');
    const description = root.querySelector('.ufc-description');
    const expiredOnly = actives.length === 0 && expireds.length > 0;
    const modal = isModalRoot(root);

    if (title) {
      if (modal) {
        title.textContent = 'Your Fuel Cash';
      } else if (expiredOnly) {
        title.textContent = 'Recently expired Fuel Cash';
      } else if (title.dataset.defaultTitle) {
        title.textContent = title.dataset.defaultTitle;
      }
    }

    if (description) {
      if (expiredOnly) {
        description.textContent = modal
          ? 'No active coupons right now. Recently expired codes are listed below.'
          : 'No active coupons to apply. Recently expired codes are listed below.';
        description.style.display = 'block';
      } else if (description.dataset.defaultDescription) {
        description.textContent = description.dataset.defaultDescription;
        description.style.display = actives.length > 0 ? 'block' : 'none';
      }
    }
  }

  function hasDisplayableFuelCash(rewards) {
    if (!Array.isArray(rewards)) return false;
    const { actives, expireds } = partitionRewards(rewards);
    return actives.length > 0 || expireds.length > 0;
  }

  function mount(options) {
    const root = typeof options.root === 'string' ? document.querySelector(options.root) : options.root;
    if (!root) {
      console.warn('UnusedFuelCashRewards.mount: root not found', options.root);
      return null;
    }
    const variant = options.variant || 'page';
    if (!root.dataset.ufcRoot) {
      root.dataset.ufcRoot = `ufc-${Math.random().toString(36).slice(2, 9)}`;
    }
    const buttonSelector = `[data-ufc-root="${root.dataset.ufcRoot}"] .unused-reward-coupon-code`;

    if (!root.dataset.ufcCartListeners) {
      setupCartUpdateListener(buttonSelector);
      root.dataset.ufcCartListeners = 'true';
    }

    const instance = {
      root,
      variant,
      load: () => render(root, variant)
    };

    if (variant === 'page') {
      instance.load();
    }

    return instance;
  }

  window.UnusedFuelCashRewards = {
    mount,
    render,
    partitionRewards,
    hasDisplayableFuelCash
  };
})();
